import {
    Vegposisjon,
    Kryssystem,
    Sideanlegg,
    Strekning,
    Vegsystem,
    Vegsystemreferanse,
} from '@/domain/omrader';
import { Vegkategori } from '@/domain/Vegkategori';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';

type RegExpGroups = Record<string, string | undefined>;

type VegsystemGroups = RegExpGroups & {
    kommune?: string;
    kortnavn?: string;
    fase?: string;
    nummer?: string;
};

type VegsystemreferanseGroups = RegExpGroups & {
    vegsystem?: string;
    strekning?: string;
    kryssystem?: string;
    sideanlegg?: string;
};

type NullableVegsystemreferanseConstructor = new (
    vegsystem: Vegsystem,
    strekning: Strekning | null,
    kryssystem: Kryssystem | null,
    sideanlegg: Sideanlegg | null,
    metrertLokasjon: null,
    kortform: string,
    kommune: number | null
) => Vegsystemreferanse;

type NullableStrekningConstructor = new (
    strekningsNummer: Vegposisjon | null,
    delstrekningsNummer?: Vegposisjon | null,
    arm?: boolean | null,
    adskilte_løp?: 'Med' | 'Mot' | 'Nei' | null,
    adskilte_løp_nummer?: number | null,
    trafikantgruppe?: string | null,
    meterAvgrensning?: Vegposisjon | null,
    retning?: 'MED' | 'MOT' | null
) => Strekning;

type NullableKryssystemConstructor = new (
    kryssystem: number | null,
    kryssdel: Vegposisjon | null,
    trafikantgruppe?: string | null,
    meterAvgrensning?: Vegposisjon | null,
    retning?: 'MED' | 'MOT' | null
) => Kryssystem;

type NullableSideanleggConstructor = new (
    sideanlegg: number | null,
    sideanleggsdel: Vegposisjon | null,
    trafikantgruppe?: string | null,
    meterAvgrensning?: Vegposisjon | null,
    retning?: 'MED' | 'MOT' | null
) => Sideanlegg;

const NullableVegsystemreferanse =
    Vegsystemreferanse as unknown as NullableVegsystemreferanseConstructor;
const NullableStrekning = Strekning as unknown as NullableStrekningConstructor;
const NullableKryssystem = Kryssystem as unknown as NullableKryssystemConstructor;
const NullableSideanlegg = Sideanlegg as unknown as NullableSideanleggConstructor;

function getGroups<T extends RegExpGroups>(match: RegExpMatchArray | null): T | null {
    return match?.groups ? (match.groups as T) : null;
}

export function createVegrefParser(
    vegobjekttypeVegsystem: Vegobjekttype
): (input: string) => Vegsystemreferanse | null {
    const vegkategoriEgenskapstype = vegobjekttypeVegsystem.egenskapstyper.find(
        et => et.id === 11276
    );
    const faseEgenskapstype = vegobjekttypeVegsystem.egenskapstyper.find(
        et => et.id === 11278
    );
    if (!vegkategoriEgenskapstype || !faseEgenskapstype) {
        throw new Error('Vegsystemreferanse mangler nødvendige egenskapstyper');
    }

    const vegkategorier = vegkategoriEgenskapstype.tillatte_verdier;
    const vegkategorierKortnavn = vegkategorier.map(tv => tv.kortnavn).join('');

    const faser = faseEgenskapstype.tillatte_verdier.map(tv => tv.kortnavn).join('');

    const regexpForLongRoadSysRefNames = new RegExp(
        '(^(europa|riks|fylkes|kommunal|privat|skogs)\\s?ve[ig](er)?\\s?\\d*$)',
        'i'
    );

    // Vegsystem
    const vegkategoriPattern = `^(?<kommune>\\d{3,4})?(?<kortnavn>[${vegkategorierKortnavn}])`;
    const fasePattern = `[${faser}]`;
    const nummerPattern = `\\d+`;
    const vegsystemCaptureAllPattern = `(${vegkategoriPattern})(?<fase>${fasePattern})?(?<nummer>${nummerPattern})?`;
    const vegsystemGroupPattern = `(?<vegsystem>${vegkategoriPattern}${fasePattern}?(?:${nummerPattern})?)`;

    // Strekning
    const trafikantgruppePattern = 'K|G';
    const strekningPattern = 'S\\d+(?:-\\d+)?';
    const delstrekningPattern = 'D\\d+(?:-\\d+)?';
    const intervalPattern = '(\\d+)(?:-)?(\\d+)?';
    const meterPattern = 'M\\d+(?:-\\d+)?';

    const strekningCaptureAllPattern = `(${trafikantgruppePattern})?(${strekningPattern})?(${delstrekningPattern})?(${meterPattern})?`;
    const strekningFullPattern = `(?<strekning>(?:${trafikantgruppePattern})?(?:${strekningPattern})?(?:${delstrekningPattern})?(?:${meterPattern})?)?`;

    const trafikantgruppeRegExp = new RegExp(trafikantgruppePattern, 'i');
    const strekningRegExp = new RegExp(strekningPattern, 'i');
    const delstrekningRegExp = new RegExp(delstrekningPattern, 'i');
    const meterRegExp = new RegExp(meterPattern, 'i');

    // Kryssystem
    const kryssystemPattern = 'KS\\d+(?:-\\d+)?';
    const kryssdelPattern = 'KD\\d+(?:-\\d+)?';
    const kryssystemCaptureAllPattern = `(${kryssystemPattern})?(${kryssdelPattern})?(${meterPattern})?`;
    const kryssystemFullPattern = `(?<kryssystem>(?:${kryssystemPattern})?(?:${kryssdelPattern})?(?:${meterPattern})?)?`;

    const kryssystemRegExp = new RegExp(kryssystemPattern, 'i');
    const kryssdelRegExp = new RegExp(kryssdelPattern, 'i');

    // Sideanlegg
    const sideanleggPattern = 'SA\\d+(?:-\\d+)?';
    const sidedelPattern = 'SD\\d+(?:-\\d+)?';
    const sideanleggCaptureAllPattern = `(${sideanleggPattern})?(${sidedelPattern})?(${meterPattern})?`;
    const sideanleggFullPattern = `(?<sideanlegg>(?:${sideanleggPattern})?(?:${sidedelPattern})?(?:${meterPattern})?)?`;

    const sideanleggRegExp = new RegExp(sideanleggPattern, 'i');
    const sidedelRegExp = new RegExp(sidedelPattern, 'i');

    // Vegsystemreferanse
    const fullPattern = `${vegsystemGroupPattern}${strekningFullPattern}${kryssystemFullPattern}${sideanleggFullPattern}$`;
    const fullRegExp = new RegExp(fullPattern, 'i');

    const roadcategoryFromShortName = (word: string): Vegkategori | null => {
        const vegkategori = vegkategorier.find(vk => vk.kortnavn === word);
        return vegkategori
            ? new Vegkategori(String(vegkategori.verdi), vegkategori.kortnavn)
            : null;
    };
    return (input: string) => {
        const word = input.toUpperCase().replace(/\s/g, '');
        if (word.length === 1 && vegkategorierKortnavn.includes(word)) {
            return roadcategoryFromShortName(word);
        }
        // Try first to match long roadsysref name
        if (regexpForLongRoadSysRefNames.test(word)) {
            const wordAsVegRef = word[0];
            if (/\d+/.test(word)) {
                // europaveg6
                //return new Vegsystemreferanse(wordAsVegRef + /\d+/.exec(word));
            }
            return roadcategoryFromShortName(wordAsVegRef);
        } else {
            const match = fullRegExp.exec(word);
            if (match) return parseVegsystemReferanse(word, match);
        }
        return null;
    };

    function parseVegsystemReferanse(
        word: string,
        match: RegExpMatchArray
    ): Vegsystemreferanse | null {
        const groups = getGroups<VegsystemreferanseGroups>(match);
        if (!groups?.vegsystem) return null;
        const { vegsystem, strekning, kryssystem, sideanlegg } = groups;

        const kommune = parseKommune(vegsystem);
        const vegsystemParsed = parseVegsystem(vegsystem);
        if (!vegsystemParsed) return null;
        const strekningParsed = strekning ? parseStrekning(strekning) : null;
        const kryssystemParsed = kryssystem ? parseKryssystem(kryssystem) : null;
        const sideanleggParsed = sideanlegg ? parseSideanlegg(sideanlegg) : null;

        const v = new NullableVegsystemreferanse(
            vegsystemParsed,
            strekningParsed,
            kryssystemParsed,
            sideanleggParsed,
            null, // metrertLokasjon
            word.replace(/^\d{4}/, ''),
            kommune
        );
        return v;
    }

    function parseKommune(word: string): number | null {
        const groups = getGroups<VegsystemGroups>(word.match(vegsystemCaptureAllPattern));
        const kommune = groups?.kommune;
        if (kommune) {
            const number = Number.parseInt(kommune);
            if (number !== 0 && !Number.isNaN(number)) return number;
        }
        return null;
    }

    function parseVegsystem(word: string): Vegsystem | null {
        const matches = word.match(vegsystemCaptureAllPattern);
        const groups = getGroups<VegsystemGroups>(matches);
        if (!groups?.kortnavn) return null;
        const { kortnavn, fase, nummer } = groups;

        const parsedNummer = nummer ? Number.parseInt(nummer) : NaN;
        return new Vegsystem(
            kortnavn ?? null,
            fase ?? null,
            !Number.isNaN(parsedNummer) && !!parsedNummer ? parsedNummer : null
        );
    }

    function parseStrekning(word: string): Strekning {
        const matches = word.match(strekningCaptureAllPattern)?.slice(1) ?? [];

        let strekning: Vegposisjon | null = null;
        let delstrekning: Vegposisjon | null = null;
        let trafikantgruppe: string | null = null;
        let meter: Vegposisjon | null = null;

        for (const match of matches) {
            if (strekningRegExp.test(match || '')) {
                strekning = parsePosisjon(match);
            } else if (delstrekningRegExp.test(match || '')) {
                delstrekning = parsePosisjon(match);
            } else if (trafikantgruppeRegExp.test(match || '')) {
                trafikantgruppe = match;
            } else if (meterRegExp.test(match || '')) {
                meter = parsePosisjon(match);
            }
        }
        return new NullableStrekning(
            strekning,
            delstrekning,
            null,
            null,
            null,
            trafikantgruppe,
            meter,
            null
        );
    }

    function parseKryssystem(word: string): Kryssystem {
        const matches = word.match(kryssystemCaptureAllPattern)?.slice(1) ?? [];

        let kryssystem: number | null = null;
        let kryssdel: Vegposisjon | null = null;
        let meter: Vegposisjon | null = null;

        for (const match of matches) {
            if (kryssystemRegExp.test(match || '')) {
                const interval = match?.match(intervalPattern);
                if (interval?.[1]) kryssystem = Number.parseInt(interval[1]);
            } else if (kryssdelRegExp.test(match || '')) {
                kryssdel = parsePosisjon(match);
            } else if (meterRegExp.test(match || '')) {
                meter = parsePosisjon(match);
            }
        }
        return new NullableKryssystem(kryssystem, kryssdel, null, meter, null);
    }

    function parseSideanlegg(word: string): Sideanlegg {
        const matches = word.match(sideanleggCaptureAllPattern)?.slice(1) ?? [];

        let sideanlegg: number | null = null;
        let sideanleggsdel: Vegposisjon | null = null;
        let meter: Vegposisjon | null = null;

        for (const match of matches) {
            if (sideanleggRegExp.test(match || '')) {
                const interval = match?.match(intervalPattern);
                if (interval?.[1]) sideanlegg = Number.parseInt(interval[1]);
            } else if (sidedelRegExp.test(match || '')) {
                sideanleggsdel = parsePosisjon(match);
            } else if (meterRegExp.test(match || '')) {
                meter = parsePosisjon(match);
            }
        }
        return new NullableSideanlegg(sideanlegg, sideanleggsdel, null, meter);
    }

    function parsePosisjon(match: string | undefined): Vegposisjon | null {
        const interval = match?.match(intervalPattern);
        if (!interval?.[1]) return null;

        const parsedFra = Number.parseInt(interval[1]);
        const parsedTil = interval[2] ? Number.parseInt(interval[2]) : NaN;
        if (Number.isNaN(parsedFra)) return null;

        return Number.isNaN(parsedTil)
            ? new Vegposisjon(parsedFra)
            : new Vegposisjon(parsedFra, parsedTil);
    }
}
